import React from "react";

import { Link, useLocation } from "react-router-dom";
import { PiGearFineFill } from "react-icons/pi";

import { BsFillGearFill } from "react-icons/bs";

import { motion, AnimatePresence } from "framer-motion";
import {
  FaChartPie,
  FaBox,
  FaUsers,
  FaBezierCurve,
  FaRegBuilding,
  FaBook,
  FaMoneyBill,
} from "react-icons/fa";
import { FaCartShopping, FaGear, FaGears, FaPowerOff } from "react-icons/fa6";
import { TbDatabaseShare } from "react-icons/tb";
import { BsFillJournalBookmarkFill } from "react-icons/bs";
import { FaFileSignature } from "react-icons/fa6";
import { GiCash, GiFiles, GiPiggyBank, GiReceiveMoney } from "react-icons/gi";
import { FiMoreHorizontal, FiX } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import ConfirmDialog from "./ConfirmDialog";
import { clearUser } from "../slices/authSlice";
import { useLogoutUserMutation } from "../toolkitServices/userApi";
import { MdOutlineInventory } from "react-icons/md";

const links = [
  {
    name: "Dashboard",
    icon: <FaChartPie />,
    title: "dashboard",
    path: "/dashboard",
  },
  {
    name: "Stocks",
    icon: <FaBox />,
    title: "stocks",
    path: "/stocks/labelled-stock-list",
  },
  {
    name: "Sales",
    icon: <FaMoneyBill />,
    title: "sales",
    path: "/sales/sales-list",
  },
  {
    name: "Quotations",
    icon: <FaFileSignature />,
    title: "quotations",
    path: "/quotations/quotation-list",
  },
  {
    name: "Urd Purchase",
    icon: <FaCartShopping />,
    title: "purchase",
    path: "/purchase/urd-gst-list",
  },
  {
    name: "Saving Scheme",
    icon: <GiPiggyBank size={20} />,
    title: "scheme",
    path: "/scheme/gold-scheme",
  },
  {
    name: "Reports",
    icon: <GiFiles />,
    title: "reports",
    path: "/reports/stock-report",
  },
  {
    name: "Transfer",
    icon: <TbDatabaseShare />,
    title: "transfer",
    path: "/transfers",
  },
  {
    name: "Members",
    icon: <FaUsers />,
    title: "members",
    path: "/members/vendor-list",
  },
  {
    name: "Transactions",
    // icon: <BsFillJournalBookmarkFill />,
    icon: <GiReceiveMoney />,
    title: "transactions",
    path: "/transactions/customer",
  },
  {
    name: "Stock Verification",
    // icon: <BsFillJournalBookmarkFill />,
    icon: <MdOutlineInventory />,
    title: "stock verification",
    path: "/stock-validation",
  },
  {
    name: "Masters",
    icon: <FaBezierCurve />,
    title: "masters",
    path: "/masters/category-master-list",
  },
  {
    name: "Organisation",
    icon: <FaRegBuilding />,
    title: "organisation",
    path: "/organisation/company-master-list",
  },
  {
    name: "Settings",
    icon: <BsFillGearFill />,
    title: "settings",
    path: "/settings/labelled-module-fields",
  },
];

const primaryMobile = [
  links[0], // dashboard
  links[1], // stocks
  links[2], // sales
  links[4], // purchase
  { name: "More", icon: <FiMoreHorizontal />, title: "more", path: "#" },
];

export default function Sidebar() {
  const location = useLocation();
  const dispatch = useDispatch();
  const [logoutUser] = useLogoutUserMutation();
  const screenType = useSelector((state) => state?.globalVariables?.screenType);
  const [moreOpen, setMoreOpen] = React.useState(false);
  const [logoutOpen, setLogoutOpen] = React.useState(false);

  const isActive = (link) => location.pathname.includes(link.title);

  return (
    <>
      {/* ✅ Desktop sidebar */}
      <motion.div
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.35 }}
        className={`hidden md:flex w-14 bg-[#9481ce] h-dvh flex-col items-center py-4 rounded-r-3xl shadow-lg ${
          screenType === "expanded" ? "hidden" : ""
        }`}
      >
        {/* 🔝 links */}
        <div className="flex flex-col items-center space-y-[2px]">
          {links.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              title={link.name}
              className={[
                "group relative flex items-center justify-center",
                "text-white text-lg p-2 rounded-full",
                "transition-all duration-200 ease-out",
                "outline-none border-none",
                "hover:bg-white/20 hover:scale-105",
                "focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-0",
                "active:scale-95",
                isActive(link)
                  ? "bg-white/25 shadow-[0_0_0_1px_rgba(255,255,255,0.10),0_8px_20px_rgba(0,0,0,0.18)]"
                  : "bg-transparent",
              ].join(" ")}
            >
              <span
                className={[
                  "transition-transform duration-200 ease-out",
                  isActive(link) ? "scale-110" : "group-hover:scale-105",
                ].join(" ")}
              >
                {link.icon}
              </span>
            </Link>
          ))}
        </div>

        {/* 🔻 logout */}
        <div className="mt-auto mb-2">
          <button
            title="Logout"
            onClick={() => setLogoutOpen(true)}
            className="group relative flex items-center justify-center text-white text-lg p-2.5 rounded-full transition-all duration-200 ease-out outline-none border-none bg-transparent hover:bg-red-500/20 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-0 active:scale-95"
          >
            <span className="transition-transform duration-200 ease-out group-hover:scale-105">
              <FaPowerOff />
            </span>
          </button>
        </div>
      </motion.div>

      {/* ✅ Mobile bottom nav */}
      {screenType !== "expanded" && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-50">
          <div className="mx-2 mb-2 rounded-2xl bg-white/80 backdrop-blur-xl ring-1 ring-violet-200 shadow-2xl">
            <div className="grid grid-cols-5 px-2 py-2">
              {primaryMobile.map((link) => {
                const active = link.title !== "more" && isActive(link);
                const onClick = (e) => {
                  if (link.title === "more") {
                    e.preventDefault();
                    setMoreOpen(true);
                  }
                };

                return (
                  <Link
                    key={link.title}
                    to={link.path}
                    onClick={onClick}
                    className={[
                      "flex flex-col items-center justify-center gap-1 py-2 rounded-xl",
                      "text-xs font-medium transition",
                      active
                        ? "bg-violet-50 text-[#4b3d91]"
                        : "text-gray-600 hover:bg-gray-50",
                    ].join(" ")}
                  >
                    <span
                      className={active ? "text-[#9481ce]" : "text-[#7d70b2]"}
                    >
                      {link.icon}
                    </span>
                    <span className="text-[10px]">{link.name}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ✅ Mobile “More” drawer */}
      <AnimatePresence>
        {moreOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMoreOpen(false)}
              className="md:hidden fixed inset-0 z-[60] bg-black/35"
            />
            <motion.div
              initial={{ y: 30, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 30, opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.18 }}
              className="md:hidden fixed bottom-0 left-0 right-0 z-[70] rounded-t-3xl bg-white shadow-2xl ring-1 ring-violet-200 overflow-hidden"
            >
              <div className="px-4 py-3 flex items-center justify-between bg-gradient-to-r from-[#7f6bd6] via-[#a88ad9] to-[#f2d6b3] text-white">
                <div className="font-semibold">Menu</div>
                <button
                  onClick={() => setMoreOpen(false)}
                  className="p-2 rounded-full bg-white/20"
                >
                  <FiX />
                </button>
              </div>

              <div className="p-3 max-h-[70vh] overflow-auto">
                <div className="grid grid-cols-2 gap-2">
                  {links?.map((link) => {
                    const active = isActive(link);
                    return (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setMoreOpen(false)}
                        className={[
                          "flex items-center gap-3 p-3 rounded-2xl transition ring-1",
                          active
                            ? "bg-violet-50 ring-violet-200 text-[#4b3d91]"
                            : "bg-white ring-gray-100 hover:bg-gray-50 text-gray-700",
                        ].join(" ")}
                      >
                        <div className="h-10 w-10 rounded-xl bg-violet-50 grid place-items-center text-[#7d70b2]">
                          {link.icon}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold truncate">
                            {link.name}
                          </div>
                          <div className="text-[11px] text-gray-500 truncate">
                            {link.path.substring(1, 20)}{" "}
                            {/* show first part of path as subtitle */}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>

              <div className="px-4 py-3 text-center text-[11px] text-gray-500 bg-white/70">
                Tap outside to close
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <ConfirmDialog
        open={logoutOpen}
        onCancel={() => setLogoutOpen(false)}
        onConfirm={async () => {
          try {
            await logoutUser().unwrap();
            dispatch(clearUser());
            window.location.replace("/login");
          } catch (err) {
            console.error("Logout failed", err);
          }
          setLogoutOpen(false);
        }}
        title="Logout?"
        message="Are you sure you want to logout?"
        confirmText="Logout"
        cancelText="Cancel"
        destructive
        icon={
          <div className="h-10 w-10 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center">
            <FaPowerOff size={24} />
          </div>
        }
      />
    </>
  );
}
